import React, { useState, useRef, useCallback } from 'react';
import { Modal, Button, Form, ProgressBar, Table, Badge, Alert } from 'react-bootstrap';
import {
    LuCloudUpload, LuDownload, LuFileSpreadsheet,
    LuCircleCheck, LuCircleX, LuTag, LuImage,
    LuX, LuChevronDown, LuChevronUp, LuInfo,
} from 'react-icons/lu';
import axios from 'axios';
import { toast } from 'react-toastify';

const baseURL = process.env.REACT_APP_BASEURL;
const MAX_IMAGES_PER_ROW = 5;

// Client-side CSV parser — extracts row names + image URLs from the `image` column
function parseCsvRows(text) {
    try {
        const lines = text.split(/\r?\n/).map(l => l.trim()).filter(Boolean);
        if (lines.length < 2) return [];

        // Find column indices from header
        const headers = lines[0].split(',').map(h => h.trim().toLowerCase());
        const nameIdx  = headers.indexOf('name');
        let imageIdx = headers.indexOf('image');
        if (imageIdx === -1) {
            imageIdx = headers.indexOf('images');
        }

        return lines.slice(1).map((line, idx) => {
            // Respect quoted fields: simple split on comma outside quotes
            const cols = line.match(/(?:"[^"]*"|[^,])+/g)?.map(c => c.trim().replace(/^"|"$/g, '')) ?? line.split(',');
            const name = nameIdx >= 0 ? (cols[nameIdx]?.trim() || `Row ${idx + 2}`) : `Row ${idx + 2}`;

            // Parse pipe-separated image URLs from the image column
            const csvImageUrls = imageIdx >= 0 && cols[imageIdx]
                ? cols[imageIdx].split('|').map(u => u.trim()).filter(u => u.startsWith('http')).slice(0, 5)
                : [];

            return { rowNum: idx + 2, name, csvImageUrls };
        });
    } catch {
        return [];
    }
}

const INITIAL_STATE = {
    show: false,
    csvFile: null,
    parsedRows: [],          // [{ rowNum, name, csvImageUrls }] — for image assignment UI
    rowImages: {},           // { [rowNum]: File[] }  — locally-attached image files
    expandedRows: {},        // { [rowNum]: boolean } — accordion open/close
    uploading: false,
    progress: 0,
    result: null,
    error: null,
};

export default function BulkUploadModal({ onUploadComplete }) {
    const [state, setState] = useState(INITIAL_STATE);
    const csvInputRef = useRef(null);
    const token = localStorage.getItem('token');

    // --- Download CSV Template ---
    const handleDownloadTemplate = async () => {
        try {
            const response = await axios.get(`${baseURL}/product/bulk/template`, {
                headers: { Authorization: `Bearer ${token}` },
                responseType: 'blob',
            });
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', 'product_upload_template.csv');
            document.body.appendChild(link);
            link.click();
            link.parentNode.removeChild(link);
        } catch {
            toast.error('Failed to download template');
        }
    };

    // --- CSV File Selection (also parses row names for image UI) ---
    const handleCsvChange = (e) => {
        const file = e.target.files[0];
        if (!file) return;
        if (!file.name.toLowerCase().endsWith('.csv')) {
            setState(s => ({ ...s, error: 'Please select a valid .csv file.', csvFile: null, parsedRows: [], rowImages: {} }));
            return;
        }
        const reader = new FileReader();
        reader.onload = (evt) => {
            const rows = parseCsvRows(evt.target.result);
            setState(s => ({ ...s, csvFile: file, parsedRows: rows, rowImages: {}, expandedRows: {}, error: null, result: null }));
        };
        reader.readAsText(file);
    };

    // --- Image Files for a Specific Row ---
    const handleImageChange = useCallback((rowNum, e) => {
        const newFiles = Array.from(e.target.files || []);
        setState(s => {
            const existing = s.rowImages[rowNum] || [];
            const combined = [...existing, ...newFiles];
            // Trim to MAX_IMAGES_PER_ROW (first 5 win)
            const trimmed = combined.slice(0, MAX_IMAGES_PER_ROW);
            const warned = combined.length > MAX_IMAGES_PER_ROW;
            if (warned) toast.info(`Only ${MAX_IMAGES_PER_ROW} images allowed per product — first ${MAX_IMAGES_PER_ROW} kept.`);
            return { ...s, rowImages: { ...s.rowImages, [rowNum]: trimmed } };
        });
        // Reset input so same files can be re-selected after removal
        e.target.value = '';
    }, []);

    const handleRemoveImage = useCallback((rowNum, imgIndex) => {
        setState(s => {
            const arr = [...(s.rowImages[rowNum] || [])];
            arr.splice(imgIndex, 1);
            return { ...s, rowImages: { ...s.rowImages, [rowNum]: arr } };
        });
    }, []);

    const toggleRow = useCallback((rowNum) => {
        setState(s => ({ ...s, expandedRows: { ...s.expandedRows, [rowNum]: !s.expandedRows[rowNum] } }));
    }, []);

    // --- Upload ---
    const handleUpload = async () => {
        if (!state.csvFile) {
            setState(s => ({ ...s, error: 'Please select a CSV file first.' }));
            return;
        }

        const formData = new FormData();
        formData.append('file', state.csvFile);

        // Append images keyed by row number: images_row_{rowNum}
        let totalImages = 0;
        for (const [rowNum, files] of Object.entries(state.rowImages)) {
            const capped = files.slice(0, MAX_IMAGES_PER_ROW);
            capped.forEach(f => {
                formData.append(`images_row_${rowNum}`, f);
                totalImages++;
            });
        }

        setState(s => ({ ...s, uploading: true, progress: 0, result: null, error: null }));

        try {
            const response = await axios.post(`${baseURL}/product/bulk/upload`, formData, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'multipart/form-data',
                },
                onUploadProgress: (progressEvent) => {
                    const percent = progressEvent.total
                        ? Math.round((progressEvent.loaded * 100) / progressEvent.total)
                        : 50;
                    setState(s => ({ ...s, progress: percent }));
                },
            });

            const result = response.data.data;
            setState(s => ({ ...s, uploading: false, progress: 100, result }));

            if (result.summary.inserted > 0) {
                const imgMsg = totalImages > 0 ? ' Images are being processed in the background.' : '';
                toast.success(`${result.summary.inserted} product(s) uploaded successfully!${imgMsg}`);
                if (onUploadComplete) onUploadComplete();
            }
        } catch (err) {
            const msg = err.response?.data?.message || 'Upload failed. Please try again.';
            setState(s => ({ ...s, uploading: false, progress: 0, error: msg }));
            toast.error(msg);
        }
    };

    const handleClose = () => {
        setState(INITIAL_STATE);
        if (csvInputRef.current) csvInputRef.current.value = '';
    };

    const { show, csvFile, parsedRows, rowImages, expandedRows, uploading, progress, result, error } = state;

    const totalImagesAttached = Object.values(rowImages).reduce((acc, arr) => acc + arr.length, 0);
    const rowsWithImages = Object.keys(rowImages).filter(r => rowImages[r]?.length > 0).length;

    return (
        <>
            {/* Trigger Button */}
            <Button
                variant="outline-primary"
                onClick={() => setState(s => ({ ...s, show: true }))}
                className="d-flex align-items-center gap-2 rounded-pill px-4 py-2"
            >
                <LuCloudUpload size={18} /> Bulk Upload
            </Button>

            {/* Modal */}
            <Modal show={show} onHide={handleClose} size="lg" centered backdrop="static" scrollable>
                <Modal.Header closeButton className="border-0 pb-0">
                    <Modal.Title className="fw-bold d-flex align-items-center gap-2">
                        <LuFileSpreadsheet size={24} className="text-primary" />
                        Bulk Product Upload
                    </Modal.Title>
                </Modal.Header>

                <Modal.Body className="px-4 pb-4">

                    {/* ─── Step 1: Download Template ─── */}
                    <div className="p-4 rounded-4 bg-light mb-4">
                        <h6 className="fw-bold mb-2">Step 1 — Download Template</h6>
                        <p className="text-muted small mb-3">
                            Download the pre-formatted CSV template, fill in your products, then upload it below.
                            Required fields: <code>name</code>, <code>price</code>.
                            <br />
                            <strong>Tip:</strong> Use the <code>image</code> column to include image URLs directly in the CSV
                            (pipe-separate multiple URLs: <code>https://a.jpg|https://b.jpg</code>, max&nbsp;5).
                            Or attach images manually per row in Step&nbsp;3.
                        </p>
                        <Button
                            variant="outline-secondary"
                            size="sm"
                            className="rounded-pill px-4 d-flex align-items-center gap-2"
                            onClick={handleDownloadTemplate}
                        >
                            <LuDownload size={16} /> Download CSV Template
                        </Button>
                    </div>

                    {/* ─── Step 2: Select CSV ─── */}
                    <div className="p-4 rounded-4 border mb-4">
                        <h6 className="fw-bold mb-3">Step 2 — Select Your CSV File</h6>

                        <Form.Group className="mb-3">
                            <Form.Label className="small fw-bold text-muted text-uppercase">
                                Select File (.csv only, max 500 rows, max 5MB)
                            </Form.Label>
                            <Form.Control
                                type="file"
                                accept=".csv"
                                ref={csvInputRef}
                                onChange={handleCsvChange}
                                className="py-2"
                                disabled={uploading || !!result}
                            />
                            {csvFile && (
                                <Form.Text className="text-success mt-1 d-block">
                                    <LuCircleCheck className="me-1" />
                                    {csvFile.name} ({(csvFile.size / 1024).toFixed(1)} KB) — {parsedRows.length} product row(s) detected
                                </Form.Text>
                            )}
                        </Form.Group>
                    </div>

                    {/* ─── Step 3: Attach Images Per Row ─── */}
                    {parsedRows.length > 0 && (
                        <div className="p-4 rounded-4 border mb-4">
                            <div className="d-flex align-items-center justify-content-between mb-1">
                                <h6 className="fw-bold mb-0 d-flex align-items-center gap-2">
                                    <LuImage size={18} className="text-primary" />
                                    Step 3 — Attach Images <span className="text-muted fw-normal">(Optional)</span>
                                </h6>
                                {totalImagesAttached > 0 && (
                                    <Badge bg="primary" className="rounded-pill px-3">
                                        {totalImagesAttached} image{totalImagesAttached !== 1 ? 's' : ''} across {rowsWithImages} product{rowsWithImages !== 1 ? 's' : ''}
                                    </Badge>
                                )}
                            </div>
                            <p className="text-muted small mb-3">
                                <LuInfo size={13} className="me-1" />
                                Expand any row to attach up to <strong>{MAX_IMAGES_PER_ROW} images</strong>. Images are uploaded to Cloudinary after products are created.
                            </p>

                            <div className="d-flex flex-column gap-2">
                                {parsedRows.map(({ rowNum, name, csvImageUrls }) => {
                                    const imgs = rowImages[rowNum] || [];
                                    const isOpen = !!expandedRows[rowNum];
                                    const hasCsvImages = csvImageUrls?.length > 0;
                                    const totalForRow = imgs.length + (hasCsvImages ? csvImageUrls.length : 0);

                                    return (
                                        <div
                                            key={rowNum}
                                            className="rounded-3 border overflow-hidden"
                                            style={{ background: (imgs.length > 0 || hasCsvImages) ? '#f0f7ff' : '#fff' }}
                                        >
                                            {/* Row Header */}
                                            <button
                                                type="button"
                                                onClick={() => toggleRow(rowNum)}
                                                disabled={uploading}
                                                className="w-100 d-flex align-items-center justify-content-between px-3 py-2 border-0 bg-transparent text-start"
                                                style={{ cursor: 'pointer' }}
                                            >
                                                <div className="d-flex align-items-center gap-2 flex-wrap">
                                                    <span className="badge bg-secondary rounded-pill" style={{ fontSize: '0.7rem' }}>
                                                        Row {rowNum}
                                                    </span>
                                                    <span className="fw-medium small text-truncate" style={{ maxWidth: 200 }}>
                                                        {name}
                                                    </span>
                                                    {hasCsvImages && (
                                                        <Badge bg="info" className="rounded-pill" style={{ fontSize: '0.68rem' }}>
                                                            {csvImageUrls.length} from CSV
                                                        </Badge>
                                                    )}
                                                    {imgs.length > 0 && (
                                                        <Badge bg="success" className="rounded-pill" style={{ fontSize: '0.68rem' }}>
                                                            +{imgs.length} attached
                                                        </Badge>
                                                    )}
                                                    {totalForRow > MAX_IMAGES_PER_ROW && (
                                                        <Badge bg="warning" text="dark" className="rounded-pill" style={{ fontSize: '0.68rem' }}>
                                                            capped at {MAX_IMAGES_PER_ROW}
                                                        </Badge>
                                                    )}
                                                </div>
                                                <span className="text-muted">
                                                    {isOpen ? <LuChevronUp size={16} /> : <LuChevronDown size={16} />}
                                                </span>
                                            </button>

                                            {/* Expanded Image Area */}
                                            {isOpen && (
                                                <div className="px-3 pb-3 pt-2 border-top" style={{ background: '#fafcff' }}>

                                                    {/* ── CSV Image URL Chips ── */}
                                                    {hasCsvImages && (
                                                        <div className="mb-3">
                                                            <div className="small fw-bold text-muted text-uppercase mb-1 d-flex align-items-center gap-1">
                                                                <LuImage size={13} /> From CSV <code className="fw-normal">image</code> column
                                                            </div>
                                                            <div className="d-flex flex-wrap gap-2">
                                                                {csvImageUrls.map((url, i) => (
                                                                    <div
                                                                        key={i}
                                                                        className="rounded-3 border overflow-hidden position-relative"
                                                                        style={{ width: 72, height: 72, flexShrink: 0, background: '#e9ecef' }}
                                                                        title={url}
                                                                    >
                                                                        <img
                                                                            src={url}
                                                                            alt={`csv-img-${i}`}
                                                                            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                                                            onError={e => { e.target.style.display = 'none'; }}
                                                                        />
                                                                        <div
                                                                            className="position-absolute bottom-0 start-0 end-0 text-white text-truncate px-1"
                                                                            style={{ fontSize: '0.5rem', background: 'rgba(0,0,0,0.5)', lineHeight: 1.5 }}
                                                                        >
                                                                            URL {i + 1}
                                                                        </div>
                                                                        <Badge
                                                                            bg="info"
                                                                            className="position-absolute top-0 start-0"
                                                                            style={{ fontSize: '0.5rem', borderRadius: '0 0 6px 0', padding: '2px 4px' }}
                                                                        >
                                                                            CSV
                                                                        </Badge>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                            {totalForRow > MAX_IMAGES_PER_ROW && (
                                                                <div className="small text-warning mt-1 d-flex align-items-center gap-1">
                                                                    <LuInfo size={12} />
                                                                    Combined CSV + attached images exceed {MAX_IMAGES_PER_ROW}. Only the first {MAX_IMAGES_PER_ROW} will be stored (CSV URLs take priority).
                                                                </div>
                                                            )}
                                                        </div>
                                                    )}

                                                    {/* ── Locally-Attached File Previews ── */}
                                                    {imgs.length > 0 && (
                                                        <div className="mb-3">
                                                            <div className="small fw-bold text-muted text-uppercase mb-1 d-flex align-items-center gap-1">
                                                                <LuCloudUpload size={13} /> Attached files
                                                            </div>
                                                            <div className="d-flex flex-wrap gap-2">
                                                                {imgs.map((file, i) => {
                                                                    const previewUrl = URL.createObjectURL(file);
                                                                    return (
                                                                        <div
                                                                            key={i}
                                                                            className="position-relative rounded-3 overflow-hidden border"
                                                                            style={{ width: 72, height: 72, flexShrink: 0 }}
                                                                        >
                                                                            <img
                                                                                src={previewUrl}
                                                                                alt={file.name}
                                                                                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                                                                onLoad={() => URL.revokeObjectURL(previewUrl)}
                                                                            />
                                                                            {!uploading && !result && (
                                                                                <button
                                                                                    type="button"
                                                                                    onClick={() => handleRemoveImage(rowNum, i)}
                                                                                    className="position-absolute top-0 end-0 border-0 p-0 d-flex align-items-center justify-content-center"
                                                                                    style={{
                                                                                        width: 20, height: 20,
                                                                                        background: 'rgba(220,38,38,0.85)',
                                                                                        borderRadius: '0 0 0 6px',
                                                                                        cursor: 'pointer',
                                                                                        color: '#fff',
                                                                                    }}
                                                                                    title="Remove image"
                                                                                >
                                                                                    <LuX size={12} />
                                                                                </button>
                                                                            )}
                                                                            <div
                                                                                className="position-absolute bottom-0 start-0 end-0 text-white text-truncate px-1"
                                                                                style={{ fontSize: '0.55rem', background: 'rgba(0,0,0,0.45)', lineHeight: 1.4 }}
                                                                            >
                                                                                {file.name}
                                                                            </div>
                                                                        </div>
                                                                    );
                                                                })}
                                                            </div>
                                                        </div>
                                                    )}

                                                    {/* Add Images Input — only show if combined slots remain */}
                                                    {totalForRow < MAX_IMAGES_PER_ROW ? (
                                                        <Form.Group>
                                                            <Form.Label className="small text-muted mb-1">
                                                                Attach additional images ({totalForRow}/{MAX_IMAGES_PER_ROW} — jpeg, png, webp)
                                                            </Form.Label>
                                                            <Form.Control
                                                                type="file"
                                                                accept="image/*"
                                                                multiple
                                                                disabled={uploading || !!result}
                                                                onChange={(e) => handleImageChange(rowNum, e)}
                                                                className="py-1 small"
                                                                id={`img-input-row-${rowNum}`}
                                                            />
                                                            <Form.Text className="text-muted">
                                                                {MAX_IMAGES_PER_ROW - totalForRow} slot{MAX_IMAGES_PER_ROW - totalForRow !== 1 ? 's' : ''} remaining (CSV + attached combined). If more than {MAX_IMAGES_PER_ROW} total, only the first {MAX_IMAGES_PER_ROW} are kept.
                                                            </Form.Text>
                                                        </Form.Group>
                                                    ) : (
                                                        <div className="small text-success d-flex align-items-center gap-1 mt-1">
                                                            <LuCircleCheck size={14} /> Maximum {MAX_IMAGES_PER_ROW} images reached for this product.
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    {/* ─── Step 4: Upload ─── */}
                    <div className="p-4 rounded-4 border mb-4">
                        <h6 className="fw-bold mb-3">
                            {parsedRows.length > 0 ? 'Step 4' : 'Step 3'} — Upload
                        </h6>

                        {error && (
                            <Alert variant="danger" className="py-2 small">
                                <LuCircleX className="me-2" />{error}
                            </Alert>
                        )}

                        {uploading && (
                            <ProgressBar
                                animated
                                now={progress}
                                label={`${progress}%`}
                                className="mb-3 rounded-pill"
                                variant="primary"
                            />
                        )}

                        {!uploading && totalImagesAttached > 0 && (
                            <Alert variant="info" className="py-2 small mb-3 d-flex align-items-center gap-2">
                                <LuImage size={15} />
                                <span>
                                    <strong>{totalImagesAttached}</strong> image{totalImagesAttached !== 1 ? 's' : ''} will be uploaded with your products.
                                    Images exceeding {MAX_IMAGES_PER_ROW} per product are automatically dropped.
                                </span>
                            </Alert>
                        )}

                        <Button
                            variant={result ? "success" : "primary"}
                            className="w-100 rounded-pill py-2 fw-bold"
                            onClick={handleUpload}
                            disabled={uploading || !csvFile || !!result}
                        >
                            {uploading
                                ? `Uploading... ${progress}%`
                                : result
                                    ? <><LuCircleCheck size={18} className="me-2" />Products Uploaded Successfully</>
                                    : <><LuCloudUpload size={18} className="me-2" />Upload Products{totalImagesAttached > 0 ? ` + ${totalImagesAttached} Image${totalImagesAttached !== 1 ? 's' : ''}` : ''}</>
                            }
                        </Button>
                    </div>

                    {/* ─── Results ─── */}
                    {result && (
                        <div className="p-4 rounded-4 border">
                            <h6 className="fw-bold mb-4">Upload Results</h6>

                            {/* Summary Cards */}
                            <div className="row g-3 mb-4">
                                <div className="col-3">
                                    <div className="text-center p-3 rounded-4 bg-light">
                                        <h4 className="fw-black mb-0">{result.summary.total}</h4>
                                        <small className="text-muted fw-bold text-uppercase">Total Rows</small>
                                    </div>
                                </div>
                                <div className="col-3">
                                    <div className="text-center p-3 rounded-4" style={{ background: '#e8f5e9' }}>
                                        <h4 className="fw-black mb-0 text-success">{result.summary.inserted}</h4>
                                        <small className="text-muted fw-bold text-uppercase">Inserted</small>
                                    </div>
                                </div>
                                <div className="col-3">
                                    <div className="text-center p-3 rounded-4" style={{ background: result.summary.failed > 0 ? '#ffebee' : '#e8f5e9' }}>
                                        <h4 className={`fw-black mb-0 ${result.summary.failed > 0 ? 'text-danger' : 'text-success'}`}>
                                            {result.summary.failed}
                                        </h4>
                                        <small className="text-muted fw-bold text-uppercase">Failed</small>
                                    </div>
                                </div>
                                {result.summary.imagesQueued > 0 && (
                                    <div className="col-3">
                                        <div className="text-center p-3 rounded-4" style={{ background: '#e3f2fd' }}>
                                            <h4 className="fw-black mb-0 text-primary">{result.summary.imagesQueued}</h4>
                                            <small className="text-muted fw-bold text-uppercase">Img Batches</small>
                                        </div>
                                    </div>
                                )}
                            </div>

                            {result.summary.imagesQueued > 0 && (
                                <Alert variant="info" className="py-2 small mb-3 d-flex align-items-center gap-2">
                                    <LuImage size={15} />
                                    Images are being uploaded to Cloudinary in the background. They will appear on products within a few seconds.
                                </Alert>
                            )}

                            {/* New Categories */}
                            {result.categoriesCreated?.length > 0 && (
                                <div className="mb-4">
                                    <h6 className="small fw-bold text-muted text-uppercase mb-2 d-flex align-items-center gap-2">
                                        <LuTag size={14} /> New Categories Created
                                    </h6>
                                    <div className="d-flex flex-wrap gap-2">
                                        {result.categoriesCreated.map(name => (
                                            <Badge key={name} bg="info" className="rounded-pill px-3 py-2 fw-normal">
                                                {name}
                                            </Badge>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Failure Table */}
                            {result.failures?.length > 0 && (
                                <div>
                                    <h6 className="small fw-bold text-danger text-uppercase mb-2 d-flex align-items-center gap-2">
                                        <LuCircleX size={14} /> Failed Rows
                                    </h6>
                                    <div style={{ maxHeight: '240px', overflowY: 'auto' }}>
                                        <Table size="sm" className="border rounded-4 overflow-hidden small">
                                            <thead className="table-light">
                                                <tr>
                                                    <th>Row</th>
                                                    <th>Name</th>
                                                    <th>Reason</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {result.failures.map((f, i) => (
                                                    <tr key={i}>
                                                        <td className="text-muted">#{f.row}</td>
                                                        <td className="fw-medium">{f.name || '—'}</td>
                                                        <td className="text-danger">{f.reason}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </Table>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </Modal.Body>
            </Modal>
        </>
    );
}
